import { Icon } from "@mui/material";
import React, { useState } from "react";

import "../../shared/css/basicStyles.css";
import "../../shared/css/ui.css";
import "../css/Collection.css";

import DefaultCoverV2 from "../../shared/cover/DefaultCoverV2";
import { Collection } from "../../shared/types/Collection";
import { Serie } from "../../shared/types/Serie";
import CollectionTopbar from "./CollectionTopbar";

const S3_COLLECTIONS = "https://newbrand.s3.eu-west-3.amazonaws.com/collections";

type Props = {
  data: { collection: Collection; series: Serie[] } | undefined;
  isLoading: boolean;
  hasError: boolean;
  onClickSerie: (serie: Serie) => void;
  onRetry: () => void;
};

const CollectionSkeleton = () => (
  <div aria-busy="true" aria-label="Chargement de la collection">
    <div className="nbk-skeleton col-skeleton__banner" />
    <div className="col-grid">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="nbk-skeleton col-skeleton__cover" />
      ))}
    </div>
  </div>
);

const PageCollectionVisual = (props: Props) => {
  const { data, isLoading, hasError, onClickSerie, onRetry } = props;
  const [useFallbackBanner, setUseFallbackBanner] = useState(false);

  if (isLoading)
    return (
      <div className="col-page">
        <CollectionTopbar />
        <CollectionSkeleton />
      </div>
    );

  if (hasError || !data)
    return (
      <div className="col-page">
        <CollectionTopbar />
        <div className="col-state" role="alert">
          <Icon aria-hidden="true">cloud_off</Icon>
          <h1>Impossible de charger cette collection</h1>
          <p>Vérifiez votre connexion internet puis réessayez.</p>
          <button type="button" className="nbk-btn nbk-btn--primary" onClick={onRetry}>
            <Icon aria-hidden="true">refresh</Icon>
            Réessayer
          </button>
        </div>
      </div>
    );

  const { collection, series } = data;
  const banner =
    collection.imgHeader && !useFallbackBanner
      ? `${S3_COLLECTIONS}/${collection.uid}/headers/${collection.imgHeader}`
      : `${S3_COLLECTIONS}/${collection.uid}/${collection.imgCollection}`;

  return (
    <div className="col-page">
      <CollectionTopbar title={collection.name} />

      <header className="col-hero">
        <img
          className="col-hero__banner"
          src={banner}
          alt=""
          onError={() => setUseFallbackBanner(true)}
        />
        <div className="col-hero__content">
          <span className="col-hero__eyebrow">Collection</span>
          <h1 className="col-hero__title">{collection.name}</h1>
          <p className="col-hero__meta">
            {collection.publishingHouseName ? <span>{collection.publishingHouseName}</span> : null}
            <span>
              {series.length} série{series.length > 1 ? "s" : ""}
            </span>
          </p>
        </div>
      </header>

      {series.length > 0 ? (
        <section aria-labelledby="col-series-title">
          <h2 id="col-series-title" className="col-section-title">
            Toutes les séries
          </h2>
          <div className="col-grid">
            {series.map((s) => (
              <DefaultCoverV2
                key={s.uid}
                serie={s}
                onClickCover={() => onClickSerie(s)}
                style={{ minWidth: "100%", maxWidth: "100%", margin: 0 }}
              />
            ))}
          </div>
        </section>
      ) : (
        <div className="col-state">
          <Icon aria-hidden="true">auto_stories</Icon>
          <h1>Aucune série pour le moment</h1>
          <p>Les séries de cette collection arrivent bientôt.</p>
        </div>
      )}
    </div>
  );
};

export default PageCollectionVisual;
